import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '@env/environment';

export interface JwtClaims {
  sub: string;
  tenant_id?: string;
  role: string;
  country_code?: string;
  locale?: string;
  exp: number;
  iat: number;
}

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  mfaRequired?: boolean;
  challenge?: { token: string; methods: string[] };
}

/**
 * AuthService — JWT en memoria + refresh en httpOnly cookie. MFA flow:
 *   POST /auth/login → si mfaRequired:true devuelve challenge
 *   POST /auth/mfa/verify con challenge.token + code → emite JWT final
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly _claims = signal<JwtClaims | null>(null);
  private accessToken: string | null = null;

  readonly claims = this._claims.asReadonly();
  readonly isAuthenticated = computed(() => this._claims() !== null);
  readonly role = computed(() => this._claims()?.role ?? null);
  readonly tenantId = computed(() => this._claims()?.tenant_id ?? null);

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/auth/login`, { email, password })
      .pipe(tap((res) => !res.mfaRequired && this.applyToken(res.accessToken)));
  }

  verifyMfa(challengeToken: string, code: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/auth/mfa/verify`, { challengeToken, code })
      .pipe(tap((res) => this.applyToken(res.accessToken)));
  }

  refresh(): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/auth/refresh`, {}, { withCredentials: true })
      .pipe(tap((res) => this.applyToken(res.accessToken)));
  }

  logout(): void {
    this.accessToken = null;
    this._claims.set(null);
    this.http
      .post(`${environment.apiBaseUrl}/auth/logout`, {}, { withCredentials: true })
      .subscribe({
        complete: () => this.router.navigate(['/login']),
      });
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private applyToken(token: string): void {
    this.accessToken = token;
    this._claims.set(this.decode(token));
  }

  private decode(token: string): JwtClaims | null {
    try {
      const [, payload] = token.split('.');
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as JwtClaims;
    } catch {
      return null;
    }
  }
}
